<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Contact Us - Get in Touch with Vedware"
      description="Ready to start your next project? Contact our expert team for web development, digital solutions, and consultation. We respond within 24 hours."
      :keywords="['contact', 'get in touch', 'web development consultation', 'project inquiry', 'support']"
      url="https://vedware.com/contact"
    />

    <!-- Hero Section -->
    <div class="relative overflow-hidden bg-gradient-to-br from-accent/5 via-gradient-start/5 to-gradient-end/5 pt-36 dark:from-accent/10 dark:via-gradient-start/10 dark:to-gradient-end/10">
      <!-- Background Elements -->
      <div class="absolute inset-0 opacity-30">
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-accent/5 to-transparent" />
      </div>

      <!-- Floating Elements -->
      <div class="absolute left-10 top-20 size-20 animate-float rounded-full bg-gradient-to-r from-accent/20 to-gradient-end/20 blur-xl" />
      <div class="absolute right-20 top-40 size-32 animate-float rounded-full bg-gradient-to-r from-gradient-start/20 to-accent/20 blur-2xl" style="animation-delay: 2s;" />
      <div class="absolute bottom-20 left-1/4 size-16 animate-float rounded-full bg-gradient-to-r from-gradient-end/20 to-accent/20 blur-lg" style="animation-delay: 4s;" />

      <div class="container relative mx-auto px-6 py-16">
        <div class="mx-auto max-w-7xl">
          <div class="mb-14 text-center">
            <h1 class="mb-6 text-4xl font-bold text-ternary-dark dark:text-primary-light sm:text-5xl lg:text-6xl">
              Let's <span class="bg-gradient-to-r from-accent to-gradient-end bg-clip-text text-transparent">Connect</span>
            </h1>
            <p class="mx-auto mb-8 max-w-3xl text-xl text-gray-600 dark:text-gray-400">
              Ready to bring your ideas to life? We'd love to hear about your project and discuss how we can help you achieve your goals.
            </p>
          </div>

          <div class="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-2">
            <!-- Contact Form -->
            <div class="order-1">
              <div class="group relative overflow-hidden rounded-3xl bg-card-gradient p-8 shadow-soft-lg transition-all duration-500 hover:shadow-glow dark:bg-card-gradient-dark lg:p-10">
                <!-- Decorative Elements -->
                <div class="absolute -right-4 -top-4 size-24 rounded-full bg-gradient-to-r from-accent/10 to-gradient-end/10 blur-2xl" />
                <div class="absolute -bottom-4 -left-4 size-20 rounded-full bg-gradient-to-r from-gradient-start/10 to-accent/10 blur-xl" />

                <div class="relative">
                  <div class="mb-8">
                    <div class="mb-4 flex flex-col sm:flex-row sm:items-center">
                      <div class="mb-4 flex size-12 items-center justify-center rounded-full bg-gradient-to-r from-accent to-gradient-end sm:mb-0 sm:mr-4">
                        <PhPaperPlaneTilt class="size-6 text-white" />
                      </div>
                      <div class="flex-1">
                        <h2 class="text-3xl font-bold text-ternary-dark dark:text-primary-light">
                          Send us a Message
                        </h2>
                        <p class="text-gray-600 dark:text-gray-400">
                          Fill out the form below and we'll get back to you as soon as possible.
                        </p>
                      </div>
                    </div>
                  </div>

                  <ContactForm v-slot="{ loading }">
                    <div class="mt-8">
                      <Btn type="submit" :loading="loading" class="w-full sm:w-auto">
                        <span v-if="!loading" class="flex items-center">
                          <PhPaperPlaneTilt class="mr-2 size-4" />
                          Send Message
                        </span>
                        <span v-else class="flex items-center">
                          <div class="mr-2 size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Sending...
                        </span>
                      </Btn>
                    </div>
                  </ContactForm>
                </div>
              </div>
            </div>

            <!-- Contact Details -->
            <div class="order-2">
              <ContactDetails :contacts="contacts" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Info Section -->
    <div class="relative overflow-hidden bg-gradient-to-r from-accent/5 to-gradient-end/5 py-20 dark:from-accent/10 dark:to-gradient-end/10">
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-20">
        <div class="absolute inset-0" style="background-image: radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(118, 75, 162, 0.1) 0%, transparent 50%);" />
      </div>

      <div class="container relative mx-auto px-6">
        <div class="mx-auto max-w-6xl text-center">
          <div class="mb-16">
            <h2 class="mb-6 text-4xl font-bold text-ternary-dark dark:text-primary-light sm:text-5xl">
              Why Choose <span class="bg-gradient-to-r from-accent to-gradient-end bg-clip-text text-transparent">Us?</span>
            </h2>
            <p class="mx-auto max-w-3xl text-xl text-gray-600 dark:text-gray-400">
              We're committed to delivering exceptional results and providing the best possible experience for our clients.
            </p>
          </div>

          <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div class="group relative overflow-hidden rounded-2xl bg-card-gradient p-8 shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-lg dark:bg-card-gradient-dark">
              <div class="absolute inset-0 bg-gradient-to-r from-accent/5 to-gradient-end/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div class="relative">
                <div class="mx-auto mb-6 flex size-20 items-center justify-center rounded-full bg-gradient-to-r from-accent to-gradient-end shadow-lg">
                  <PhLightning class="size-10 text-white" />
                </div>
                <h3 class="mb-4 text-2xl font-semibold text-ternary-dark dark:text-ternary-light">
                  Fast Response
                </h3>
                <p class="text-gray-600 dark:text-gray-400">
                  We typically respond to all inquiries within 24 hours, often much sooner. Your time is valuable, and we respect that.
                </p>
              </div>
            </div>

            <div class="group relative overflow-hidden rounded-2xl bg-card-gradient p-8 shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-lg dark:bg-card-gradient-dark">
              <div class="absolute inset-0 bg-gradient-to-r from-accent/5 to-gradient-end/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div class="relative">
                <div class="mx-auto mb-6 flex size-20 items-center justify-center rounded-full bg-gradient-to-r from-accent to-gradient-end shadow-lg">
                  <PhShield class="size-10 text-white" />
                </div>
                <h3 class="mb-4 text-2xl font-semibold text-ternary-dark dark:text-ternary-light">
                  Secure & Private
                </h3>
                <p class="text-gray-600 dark:text-gray-400">
                  Your information is always kept confidential and secure. We follow industry best practices for data protection.
                </p>
              </div>
            </div>

            <div class="group relative overflow-hidden rounded-2xl bg-card-gradient p-8 shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-lg dark:bg-card-gradient-dark">
              <div class="absolute inset-0 bg-gradient-to-r from-accent/5 to-gradient-end/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div class="relative">
                <div class="mx-auto mb-6 flex size-20 items-center justify-center rounded-full bg-gradient-to-r from-accent to-gradient-end shadow-lg">
                  <PhUsers class="size-10 text-white" />
                </div>
                <h3 class="mb-4 text-2xl font-semibold text-ternary-dark dark:text-ternary-light">
                  Expert Team
                </h3>
                <p class="text-gray-600 dark:text-gray-400">
                  20+ years of experience in web development and digital solutions. We bring expertise and passion to every project.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import { PhEnvelope, PhLightning, PhMapPin, PhPaperPlaneTilt, PhShield, PhUsers } from '@phosphor-icons/vue'
import ContactDetails from '~/components/Contact/ContactDetails.vue'
import ContactForm from '~/components/Shared/ContactForm.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'
import Btn from '~/components/UI/Btn.vue'

interface Contact {
  id: string
  name: string
  title: string
  icon: any
  href: string
  clickable: boolean
  ariaLabel: string
  description?: string
}

const contacts = ref<Contact[]>([
  {
    id: '1',
    name: 'Worldwide Remote',
    title: 'Location',
    icon: PhMapPin,
    href: '#',
    clickable: false,
    ariaLabel: 'Our location',
    description: 'We work with clients globally from our remote office',
  },
  {
    id: '2',
    name: 'info@vedware.com',
    title: 'Email Address',
    icon: PhEnvelope,
    href: 'mailto:info@vedware.com',
    clickable: true,
    ariaLabel: 'Send us an email',
    description: 'Drop us a line anytime, we\'ll get back to you within 24 hours',
  },
])
</script>
